import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import translations from './translations.json';
import { CONTENT_ROUTES } from '../content/publicContent';

export const BASE_URL = 'https://line-ranger-guild-war.onrender.com';
export const DEFAULT_LOCALE = 'ja';
export const LOCALE_STORAGE_KEY = 'rangers-preferred-locale';
export const SUPPORTED_LOCALES = ['ja', 'ko', 'zh-tw', 'th', 'en'];

export const PUBLIC_ROUTES = CONTENT_ROUTES;

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  normalizedPath: '/',
  pathLocale: null
});

export const normalizeLocale = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.toLowerCase().replace('_', '-');

  if (SUPPORTED_LOCALES.includes(normalized)) return normalized;
  if (normalized.startsWith('ja')) return 'ja';
  if (normalized.startsWith('ko')) return 'ko';
  if (normalized.startsWith('th')) return 'th';
  if (normalized.startsWith('en')) return 'en';
  if (normalized.startsWith('zh')) return 'zh-tw';

  return null;
};

export const normalizeRoutePath = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  const withoutTrailingSlash = pathname.replace(/\/+$/, '');
  return withoutTrailingSlash || '/';
};

export const getLocaleFromPath = (pathname) => {
  const firstSegment = (pathname || '/').split('/')[1];
  return normalizeLocale(firstSegment);
};

export const stripLocaleFromPath = (pathname) => {
  const pathLocale = getLocaleFromPath(pathname);
  if (!pathLocale) return normalizeRoutePath(pathname);

  const segments = pathname.split('/').slice(2);
  return normalizeRoutePath(`/${segments.join('/')}`);
};

export const getLocalizedPath = (routePath, locale) => {
  const targetLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  const cleanRoute = normalizeRoutePath(routePath);
  return cleanRoute === '/' ? `/${targetLocale}` : `/${targetLocale}${cleanRoute}`;
};

export const getPublicRouteKey = (routePath) => {
  return PUBLIC_ROUTES[normalizeRoutePath(routePath)] || null;
};

export const getMessages = (locale) => {
  const targetLocale = normalizeLocale(locale) || DEFAULT_LOCALE;
  return translations[targetLocale] || translations[DEFAULT_LOCALE];
};

export const getStoredLocale = () => {
  if (typeof window === 'undefined') return null;
  return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
};

export const setStoredLocale = (locale) => {
  if (typeof window === 'undefined') return;
  const normalized = normalizeLocale(locale);
  if (normalized) {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
  }
};

const getBrowserLocale = () => {
  if (typeof window === 'undefined') return null;
  const languages = window.navigator.languages || [window.navigator.language];
  for (const language of languages) {
    const locale = normalizeLocale(language);
    if (locale) return locale;
  }
  return null;
};

const resolveLocale = (pathname) => {
  const pathLocale = getLocaleFromPath(pathname);
  if (pathLocale) return pathLocale;

  return getStoredLocale() || getBrowserLocale() || DEFAULT_LOCALE;
};

const translatePath = (messages, path, params = {}) => {
  const value = path.split('.').reduce((current, key) => current?.[key], messages);
  if (typeof value !== 'string') return value;

  return Object.entries(params).reduce(
    (text, [key, replacement]) => text.replaceAll(`{${key}}`, replacement),
    value
  );
};

export const LocaleProvider = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname || '/';

  const value = useMemo(() => {
    const locale = resolveLocale(pathname);
    return {
      locale,
      pathLocale: getLocaleFromPath(pathname),
      normalizedPath: stripLocaleFromPath(pathname)
    };
  }, [pathname]);

  useEffect(() => {
    setStoredLocale(value.locale);
  }, [value.locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);

export const useI18n = () => {
  const localeState = useLocale();
  const messages = getMessages(localeState.locale);

  return {
    ...localeState,
    messages,
    t: (path, params) => translatePath(messages, path, params)
  };
};
